import { type MutableRefObject, useEffect } from "react";

import type { EditorCommand } from "@dxf-ai-editor/contracts";

import type { DraftCommandId, DraftSession } from "../scene/draftingHelpers";
import type { EntityPreview } from "@dxf-ai-editor/contracts";
import {
  getEntityMetadataIndex,
  selectEntitiesInActiveSpace,
  useEditorStore,
} from "../state/editorStore";
import { useT } from "../i18n";
import { useSysvarStore } from "../dynamicInput/sysvarStore";
import { getSelectionCyclingState } from "../state/selectionCyclingState";
// Point-filter / temporary-tracking (FROM/M2P/.X.Y.Z — ТЗ §8.6/§10.4):
// canvas Escape must cancel just the active sub-prompt, not the whole draft
// (Codex PR #504 P2).
import { clearPointFilter, isPointFilterActive } from "../scene/pointFilterSession";
// OTRACK-SUBSYSTEM — `TT` (Temporary Track Point): canvas Escape must
// cancel just the active TT sub-prompt (mirrors the point-filter import
// above; separate file, never edits `pointFilterSession.ts`).
import {
  clearTemporaryTrackPoint,
  isTemporaryTrackPointActive,
} from "../scene/otrackTrackPointSession";
import {
  resolveShellDraftCommand,
  isExactShellKeyboardAlias,
  isShellKeyboardCharacter,
  isEditableTarget,
} from "./useCommandLine";

export type KeyboardShortcutDeps = {
  commandText: string;
  draftSession: DraftSession | null;
  selectedEntities: EntityPreview[];
  lastSubmittedCommand: string | null;
  /**
   * Codex round 3 #6 (PR #87) — function-valued ref. The Ctrl+S handler
   * invokes this on press so the heavy DXF serialisation (cache-walk
   * + writer pass) runs only when the user actually presses Save.
   * Previously this was a `MutableRefObject<string>` populated by a
   * useMemo that recomputed on every `entityMetadataIndexVersion` bump,
   * which serialised the full 685k-entity document on every mutation.
   */
  exportedDxfTextRef: MutableRefObject<() => string>;
  documentTitleRef: MutableRefObject<string>;
  submitCommandTextRef: MutableRefObject<
    (rawText: string, source?: "user" | "batch") => boolean
  >;
  beginDraftingRef: MutableRefObject<
    (command: DraftCommandId, options?: Record<string, string>) => void
  >;
  dispatch: (command: EditorCommand) => void;
  setDraftSession: (session: DraftSession | null) => void;
  setCommandText: (text: string | ((prev: string) => string)) => void;
  setCommandHistoryOpen: (open: boolean | ((prev: boolean) => boolean)) => void;
  appendCommandFeed: (text: string) => void;
  /**
   * CECOLOR round-8 FINDING A — the dialog-less `-COLOR` keyword prompt arms
   * `colorPromptPending` (App.tsx), and the pending-reply consumer at the TOP of
   * `submitCommandText` consumes the NEXT submission as a colour reply. Escape
   * must be able to CANCEL that prompt (AutoCAD parity): without clearing the
   * flag here, `-COLOR` then Esc leaves the prompt armed and the next normal
   * command is wrongly swallowed by the reply resolver. The flag + setter are
   * threaded in so the Escape handler clears it (and the early-return guard does
   * NOT bail while a prompt is pending with no draft/text/selection).
   */
  colorPromptPending: boolean;
  /**
   * CECOLOR round-9 ROOT CURE — the SINGLE shared colour-input-context clearer
   * (App.tsx `clearColorTransientState`): closes the command-line COLOR picker,
   * clears `colorPromptPending`, and bumps the swatch reset epoch (closing the
   * ribbon swatch's local picker). Escape ends the colour-input context like
   * every other seam, so it calls this instead of clearing only the pending flag
   * (round-8 A generalised — Escape now also closes a still-open picker). The
   * `colorPromptPending` boolean above is still threaded so the early-return
   * guard knows a prompt is active and does NOT bail while one is pending.
   */
  clearColorTransientState: () => void;
};

/**
 * Global keyboard shortcut handler extracted from App.tsx.
 * Handles Ctrl+S (DXF download), Ctrl+A (select all), Escape,
 * Enter (repeat last / submit), Backspace, Delete, and
 * single-key command aliases (AutoCAD parity).
 */
export function useKeyboardShortcuts(deps: KeyboardShortcutDeps): void {
  const t = useT();
  const {
    commandText,
    draftSession,
    selectedEntities,
    lastSubmittedCommand,
    exportedDxfTextRef,
    documentTitleRef,
    submitCommandTextRef,
    beginDraftingRef,
    dispatch,
    setDraftSession,
    setCommandText,
    setCommandHistoryOpen,
    appendCommandFeed,
    colorPromptPending,
    clearColorTransientState,
  } = deps;

  useEffect(() => {
    function handleGlobalKeyDown(event: KeyboardEvent) {
      if (event.defaultPrevented) {
        return;
      }

      // Ctrl/Cmd keyboard shortcuts (AutoCAD parity).
      if (event.ctrlKey || event.metaKey) {
        const key = event.key.toLowerCase();

        if (key === "s") {
          // Ctrl+S: Download DXF (mirrors the DXF download button).
          // Codex round 3 #6 — build the DXF on press only.
          event.preventDefault();
          const dxfText = exportedDxfTextRef.current();
          const blob = new Blob([dxfText], {
            type: "application/dxf;charset=utf-8",
          });
          const url = URL.createObjectURL(blob);
          const anchor = document.createElement("a");
          anchor.href = url;
          anchor.download = `${(documentTitleRef.current || "drawing").replace(/[^a-zA-Z0-9_-]/gu, "_")}.dxf`;
          anchor.click();
          URL.revokeObjectURL(url);
          return;
        }

        if (key === "w" && !event.shiftKey) {
          // Ctrl+W: AutoCAD parity — toggle SELECTIONCYCLING between
          // 2 (badge + listbox dialog) and 0 (off). AutoCAD's status
          // bar adornment toggles only between these two values; the
          // intermediate "1 = badge only" is reached via OPTIONS, not
          // the shortcut.
          event.preventDefault();
          const sysvarStore = useSysvarStore.getState();
          const next = sysvarStore.values.SELECTIONCYCLING === 0 ? 2 : 0;
          sysvarStore.setVar("SELECTIONCYCLING", next);
          appendCommandFeed(
            t("audit.selectioncycling_toggle", { value: next }),
          );
          return;
        }

        if (key === "a" && event.shiftKey) {
          // Ctrl+Shift+A: AutoCAD `SELECT Previous` — restore the prior
          // pickset (state.selection.previousPickset) into entityIds.
          // The reducer (`selection/restore-previous`) is idempotent —
          // calling twice yields the same selection. AutoCAD parity:
          // pressing the shortcut a second time would normally swap
          // back, but AutoCAD's SELECT P is one-way restore, NOT a
          // toggle — match AutoCAD by leaving previousPickset untouched
          // and let users press Ctrl+A to extend to all entities.
          event.preventDefault();
          dispatch({
            type: "selection/restore-previous",
            source: "scene",
          });
          return;
        }

        if (key === "a" && !event.shiftKey) {
          // Ctrl+A: Select all entities in the active space.
          //
          // PR-V6 — on huge imports `state.drawing.entities` is empty
          // (the post-cap reactive list lives in `__entitiesCache` and
          // the metadata index, not in zustand state). Falling back to
          // `selectEntitiesInActiveSpace` would yield 0 ids and feel
          // like a no-op to the user, so we read directly from the
          // metadata index whenever the reactive list is empty AND
          // metadata records exist. Smaller drawings stay on the
          // existing path so DimStyle / Multileader unit tests that
          // pre-seed `state.drawing.entities` keep working unchanged.
          event.preventDefault();
          const editorState = useEditorStore.getState().state;
          let allIds: string[];
          const metadataIndex = getEntityMetadataIndex();
          if (
            editorState.drawing.entities.length === 0 &&
            metadataIndex.size > 0
          ) {
            const records = metadataIndex.filterBySpace(
              editorState.drawing.activeSpaceId,
            );
            allIds = new Array<string>(records.length);
            for (let i = 0; i < records.length; i++) {
              allIds[i] = records[i]!.id;
            }
          } else {
            allIds = selectEntitiesInActiveSpace(editorState).map((e) => e.id);
          }
          dispatch({
            type: "shell/select-entities",
            source: "scene",
            entityIds: allIds,
          });
          return;
        }

        if (key === "z" && !event.shiftKey) {
          // Ctrl+Z / Cmd+Z — skip when focus is in an editable element
          if (isEditableTarget(event.target)) return;
          event.preventDefault();
          dispatch({ type: "history/undo", source: "toolbar" });
          return;
        }

        if ((key === "z" && event.shiftKey) || key === "y") {
          // Ctrl+Shift+Z / Cmd+Shift+Z / Ctrl+Y / Cmd+Y — redo
          if (isEditableTarget(event.target)) return;
          event.preventDefault();
          dispatch({ type: "history/redo", source: "toolbar" });
          return;
        }

        // Let other Ctrl+key combinations pass through (Ctrl+C copy text, etc.)
        return;
      }

      if (event.altKey) {
        return;
      }

      const targetIsEditable = isEditableTarget(event.target);
      const targetElement =
        event.target instanceof HTMLElement ? event.target : null;
      const isCommandInputTarget = targetElement?.id === "command-input";

      if (event.key === "Escape") {
        if (targetIsEditable && !isCommandInputTarget) {
          return;
        }

        // OTRACK-SUBSYSTEM — `TT` (Temporary Track Point): Escape must
        // cancel just the active TT sub-prompt, mirroring the point-filter
        // branch immediately below (and checked FIRST, matching the state
        // machines' own TT-before-point-filter interception order — see
        // `otrackTrackPointSession.ts`).
        if (draftSession && isTemporaryTrackPointActive(draftSession)) {
          event.preventDefault();
          // Local Codex review round 5 P2 — mirrors the point-filter
          // branch's SAME clear immediately below (local Codex round-10
          // P2): an Extended (Apparent) Intersection override can be armed
          // (or even captured) WHILE a `TT` sub-prompt is active, same as
          // while a point filter is active — `clearTemporaryTrackPoint`
          // resumes the SAME host command/step in place, so nothing else
          // clears a stale override. Without this, the next canvas click
          // (meant to resume the host prompt) is silently swallowed as a
          // stale operand pick instead.
          if (useSysvarStore.getState().osnapExtendedOverride != null) {
            useSysvarStore.getState().setOsnapExtendedOverride(null);
          }
          setDraftSession(clearTemporaryTrackPoint(draftSession));
          return;
        }

        // Point-filter / temporary-tracking (FROM/M2P/.X.Y.Z): Escape must
        // cancel just the active sub-prompt and return to the host
        // command's own point prompt (AutoCAD parity) — not abort the
        // whole draft via the full clear below. Mirrors the SAME `\x1b`
        // cancel `advancePointFilterWithText` already applies when Escape
        // reaches it through the DI-input path; this is the canvas-focus
        // path, which never routes through that text pipeline at all.
        // (Codex PR #504 P2 — "Route canvas Escape through the active
        // filter".)
        if (draftSession && isPointFilterActive(draftSession)) {
          event.preventDefault();
          // local Codex round-10 P2 — an Extended (Apparent) Intersection
          // override can be ARMED (or even captured) WHILE a point filter
          // is active: the Shift+right-click context menu that arms it
          // reaches the canvas regardless of an in-flight FROM/M2P/.X.Y.Z
          // sub-prompt. `clearPointFilter` below returns to the SAME host
          // command/step in place — command unchanged, step unchanged, no
          // point committed — so NONE of the App-level lifecycle effect's
          // triggers (commandChanged / point-count-changed / !acceptsPoint)
          // ever fire for it. Clear the override HERE, in the same branch,
          // before the point-filter cancel — otherwise the override
          // survives as a landmine and the next canvas click (meant to
          // resume the host command's own point prompt) is silently
          // swallowed as a stale operand pick instead.
          if (useSysvarStore.getState().osnapExtendedOverride != null) {
            useSysvarStore.getState().setOsnapExtendedOverride(null);
          }
          setDraftSession(clearPointFilter(draftSession));
          return;
        }

        // W2.5 OSNAP-OVERRIDE-MODES-S2 — same shape as the point-filter
        // branch above: Escape cancels an armed-but-uncaptured (or
        // captured-but-uncommitted) Extended (Apparent) Intersection
        // override WITHOUT aborting an unrelated active draft session. The
        // override does not depend on draft-anchored state, so this check
        // runs regardless of `draftSession`.
        if (useSysvarStore.getState().osnapExtendedOverride != null) {
          event.preventDefault();
          useSysvarStore.getState().setOsnapExtendedOverride(null);
          return;
        }

        // AutoCAD parity: Escape clears the current entity selection as
        // well as any in-flight draft session or command line text. This
        // matches the "Select objects" prompt behaviour — pressing
        // Escape returns the user to an empty selection ready for the
        // next pick / CHPROP / MATCHPROP run.
        //
        // PR-V6 — `selectedEntities` is derived from
        // `state.drawing.entities` (which may be empty on huge imports);
        // the actual selection lives in `state.selection.entityIds`.
        // Read the raw id list so Esc clears the bulk selection from
        // Ctrl+A on huge-import drawings too.
        const rawSelectionIds =
          useEditorStore.getState().state.selection.entityIds;
        const hasSelection =
          selectedEntities.length > 0 || rawSelectionIds.length > 0;
        // CECOLOR round-8 FINDING A — a pending `-COLOR` prompt is "active" too:
        // Escape must cancel it. Without `!colorPromptPending` here, `-COLOR`
        // armed with no draft/text/selection would early-return and the prompt
        // could never be cancelled with Esc (the next command would be eaten by
        // the reply resolver). AutoCAD lets Escape cancel an in-flight prompt.
        if (
          !draftSession &&
          !commandText.trim() &&
          !hasSelection &&
          !colorPromptPending
        ) {
          return;
        }

        event.preventDefault();
        setDraftSession(null);
        setCommandText("");
        setCommandHistoryOpen(false);
        // Cancel the colour-input context (round-8 FINDING A / round-9 ROOT
        // CURE) — unconditional clear is safe since the handler only reaches here
        // when something is active. Route through the shared clearer so Escape
        // tears down the SAME state as every other seam: closes the command-line
        // COLOR picker, clears the dialog-less `-COLOR` pending-reply flag, and
        // bumps the swatch reset epoch (closing the ribbon swatch's local
        // picker). After this, the next submission runs as a normal command
        // instead of being consumed by the colour-reply resolver.
        clearColorTransientState();
        if (hasSelection) {
          dispatch({
            type: "shell/clear-selection",
            source: "scene",
          });
        }
        return;
      }

      if (targetIsEditable) {
        return;
      }

      if (event.shiftKey && event.key === " ") {
        // Shift+Space — AutoCAD silent selection-cycling. Rotates the
        // selection through the current set of stacked candidates
        // under the cursor regardless of SELECTIONCYCLING value (the
        // sysvar gates the visual badge / dialog only). When no
        // stacked candidates exist (cursor over single entity or empty
        // space), the keypress is a no-op so the user can keep typing
        // command-line input freely.
        const cyclingState = getSelectionCyclingState();
        if (cyclingState.stackedCandidates.length >= 2) {
          event.preventDefault();
          const nextId = cyclingState.advanceCycle();
          if (nextId !== null) {
            dispatch({
              type: "shell/select-entities",
              source: "scene",
              entityIds: [nextId],
            });
          }
          return;
        }
      }

      if (event.key === "Enter") {
        if (!draftSession && !commandText.trim()) {
          // AutoCAD parity: Enter with empty input repeats the last
          // executed command. lastSubmitted is only set by
          // routeLayoutCommand for top-level commands (LINE, CIRCLE,
          // etc.), never for draft step inputs (coordinates).
          const lastCmd = lastSubmittedCommand;
          if (lastCmd) {
            event.preventDefault();
            appendCommandFeed(lastCmd);
            submitCommandTextRef.current(lastCmd);
          }
          return;
        }

        event.preventDefault();
        submitCommandTextRef.current(commandText);
        return;
      }

      if (event.key === "Backspace") {
        if (!commandText) {
          return;
        }

        event.preventDefault();
        setCommandText((currentText: string) => currentText.slice(0, -1));
        return;
      }

      if (event.key === "Delete") {
        if (draftSession || selectedEntities.length === 0) {
          return;
        }

        event.preventDefault();
        appendCommandFeed(t("feed.delete"));
        dispatch({
          type: "shell/delete-entities",
          source: "scene",
          entityIds: selectedEntities.map((entity) => entity.id),
        });
        return;
      }

      if (draftSession) {
        return;
      }

      if (!isShellKeyboardCharacter(event.key)) {
        return;
      }

      event.preventDefault();
      setCommandHistoryOpen(false);
      const nextBuffer = `${commandText}${event.key.toUpperCase()}`.replace(
        /\s+/gu,
        "",
      );

      if (isExactShellKeyboardAlias(nextBuffer)) {
        const draftCommand = resolveShellDraftCommand(nextBuffer);

        if (draftCommand) {
          beginDraftingRef.current(draftCommand);
          return;
        }
      }

      setCommandText(nextBuffer);
    }

    window.addEventListener("keydown", handleGlobalKeyDown);
    return () => window.removeEventListener("keydown", handleGlobalKeyDown);
  }, [
    commandText,
    dispatch,
    draftSession,
    selectedEntities,
    lastSubmittedCommand,
    exportedDxfTextRef,
    documentTitleRef,
    submitCommandTextRef,
    beginDraftingRef,
    setDraftSession,
    setCommandText,
    setCommandHistoryOpen,
    appendCommandFeed,
    colorPromptPending,
    clearColorTransientState,
    t,
  ]);
}
